import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Loader2, Sparkles, X, AlertTriangle } from 'lucide-react';

interface TaskState {
  running: boolean;
  title: string;
  description: string;
  startedAt: number;
  stages: string[];
  stageIdx: number;
  error: string | null;
}

interface TaskRunnerCtx {
  state: TaskState;
  runTask: <T>(opts: {
    title: string;
    description?: string;
    stages?: string[];
    fn: () => Promise<T>;
  }) => Promise<T>;
}

const initialState: TaskState = {
  running: false,
  title: '',
  description: '',
  startedAt: 0,
  stages: [],
  stageIdx: 0,
  error: null,
};

const Ctx = createContext<TaskRunnerCtx>({
  state: initialState,
  runTask: async () => undefined as any,
});

export function useTaskRunner() {
  return useContext(Ctx);
}

export function TaskRunnerProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TaskState>(initialState);
  const stageTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const runTask = useCallback(async <T,>(opts: {
    title: string;
    description?: string;
    stages?: string[];
    fn: () => Promise<T>;
  }): Promise<T> => {
    const stages = opts.stages && opts.stages.length > 0 ? opts.stages : ['Memproses…'];
    setState({
      running: true,
      title: opts.title,
      description: opts.description || '',
      startedAt: Date.now(),
      stages,
      stageIdx: 0,
      error: null,
    });

    // Rotate through stages while task runs (visual feedback only)
    if (stageTimerRef.current) clearInterval(stageTimerRef.current);
    stageTimerRef.current = setInterval(() => {
      setState((s) => {
        if (!s.running) return s;
        const next = Math.min(s.stages.length - 1, s.stageIdx + 1);
        return { ...s, stageIdx: next };
      });
    }, 4000);

    try {
      const result = await opts.fn();
      if (stageTimerRef.current) clearInterval(stageTimerRef.current);
      setState((s) => ({ ...s, running: false }));
      return result;
    } catch (e: any) {
      if (stageTimerRef.current) clearInterval(stageTimerRef.current);
      setState((s) => ({ ...s, running: false, error: String(e?.message || e) }));
      throw e;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (stageTimerRef.current) clearInterval(stageTimerRef.current);
    };
  }, []);

  // Block beforeunload while running
  useEffect(() => {
    if (!state.running) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state.running]);

  return (
    <Ctx.Provider value={{ state, runTask }}>
      {children}
      <GlobalTaskModal state={state} onDismissError={() => setState((s) => ({ ...s, error: null }))} />
    </Ctx.Provider>
  );
}

function GlobalTaskModal({ state, onDismissError }: { state: TaskState; onDismissError: () => void }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!state.running) {
      setElapsed(0);
      return;
    }
    const id = setInterval(() => setElapsed((Date.now() - state.startedAt) / 1000), 250);
    return () => clearInterval(id);
  }, [state.running, state.startedAt]);

  // Error toast (dismissable)
  if (!state.running && state.error) {
    return (
      <div className="fixed inset-0 z-[300] grid place-items-center backdrop-blur" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={onDismissError}>
        <div className="bg-paper border-2 border-crimson max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-crimson" />
            <h3 className="font-display font-bold text-[16px] text-crimson">Tugas Gagal</h3>
            <button onClick={onDismissError} className="ml-auto w-7 h-7 grid place-items-center border border-ink-line hover:border-crimson">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="font-mono text-[11px] text-ink-2 whitespace-pre-wrap leading-relaxed">{state.error}</p>
        </div>
      </div>
    );
  }

  if (!state.running) return null;

  const currentStage = state.stages[state.stageIdx] || state.stages[0];

  return (
    <div
      className="fixed inset-0 z-[300] grid place-items-center backdrop-blur"
      onClick={(e) => e.preventDefault()}
      onMouseDown={(e) => e.preventDefault()}
      style={{ pointerEvents: 'auto', background: 'rgba(0,0,0,0.78)' }}
    >
      <div className="bg-paper border-2 border-ink max-w-lg w-full shadow-[0_10px_40px_-10px_hsl(var(--ink)/0.5)]">
        <div className="flex items-center gap-3 px-5 py-3 border-b-2 border-ink bg-paper-2/40">
          <Sparkles className="w-4 h-4 text-brass animate-pulse" />
          <h3 className="font-display font-bold text-[15px]">{state.title}</h3>
          <span className="ml-auto font-mono text-[10px] tracking-widest uppercase text-ink-3">
            Proses berjalan
          </span>
        </div>

        <div className="p-5">
          {state.description && (
            <p className="font-serif text-[13px] text-ink-2 leading-snug mb-4">{state.description}</p>
          )}

          {/* Indeterminate progress bar */}
          <div className="h-1.5 bg-paper-2 border border-ink-line/40 relative overflow-hidden mb-3">
            <div className="absolute inset-y-0 left-0 w-1/3 bg-brass" style={{ animation: 'progressSlide 1.6s linear infinite' }} />
          </div>

          <div className="flex items-center gap-2 font-mono text-[11px] text-ink-2 mb-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-brass" />
            <span className="font-bold text-ink">{currentStage}</span>
          </div>

          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3 flex gap-3">
            <span>Elapsed: <strong className="text-ink tabular-nums">{elapsed.toFixed(1)}s</strong></span>
            <span>· Stage {state.stageIdx + 1}/{state.stages.length}</span>
          </div>

          <div className="mt-4 pt-3 border-t border-dashed border-ink-line font-mono text-[10px] uppercase tracking-widest text-ink-3 leading-relaxed">
            ⚠ Jangan tutup tab / refresh halaman. Tugas LLM lintas-batch · tidak boleh dipotong di tengah.
          </div>
        </div>
      </div>

      <style>{`
        @keyframes progressSlide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  );
}
